/**
 * A structure doc and its room docs, as the app reads them: each room's
 * collections normalized to arrays, then assembled into the one
 * structure-with-rooms shape the viewer, Schedules & Takeoffs and the
 * proposal all price and draw from.
 *
 * Pure — no I/O. In shared-lib so a Cloud Function that reads the same docs
 * with the Admin SDK builds the same shape the web app does
 * (web-app/src/services/structureService.js is the web caller).
 */

/**
 * Convert a map (object keyed by ID) to an array
 * Handles both arrays (returns as-is) and maps (converts to array)
 */
const mapToArray = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return Object.values(data);
};

/**
 * Normalize room data - converts any maps to arrays
 * Handles data that may have been saved as maps during testing
 *
 * @param {Object} room  `{ ...rooms/{id} doc data, id }`.
 */
const normalizeRoomData = (room) => {
  if (!room) return room;
  return {
    ...room,
    walls: mapToArray(room.walls),
    floors: mapToArray(room.floors),
    objects: mapToArray(room.objects),
    doors: mapToArray(room.doors),
    windows: mapToArray(room.windows),
    openings: mapToArray(room.openings),
    ceilings: mapToArray(room.ceilings),
  };
};

/**
 * Assemble the structure-doc data + its rooms into the shape the 3D viewer
 * expects. Shared by the one-shot `getStructureWithRooms` and the live
 * `subscribeStructureWithRooms` so both paths stay in lock-step.
 *
 * The original structure-level walls (3D boxes with thickness) are preserved as
 * `structureWalls` since `walls` is overridden with the flattened room walls
 * (zero-thickness planes). Likewise `structureDoors / Windows / Openings` keep
 * the structure-level copies (parentWallIds → structure wall id) for the
 * structure-wall box CSG cutouts.
 *
 * @param {Object} structureData  `{ id, ...structures/{id} doc data }`.
 * @param {Array}  rooms          Normalized room docs for the structure.
 * @returns {Object} structureWithRooms
 */
const assembleStructureWithRooms = (structureData, rooms) => ({
  ...structureData,
  structureWalls: Array.isArray(structureData.walls) ? structureData.walls : [],
  structureDoors: Array.isArray(structureData.doors) ? structureData.doors : [],
  structureWindows: Array.isArray(structureData.windows)
    ? structureData.windows
    : [],
  structureOpenings: Array.isArray(structureData.openings)
    ? structureData.openings
    : [],
  rooms,
  // For compatibility with existing code that expects certain fields
  walls: rooms.flatMap((room) => room.walls || []),
  floors: rooms.flatMap((room) => room.floors || []),
  // Keep objects empty at structure level - they should be accessed per room
  objects: [],
  doors: rooms.flatMap((room) => room.doors || []),
  windows: rooms.flatMap((room) => room.windows || []),
  openings: rooms.flatMap((room) => room.openings || []),
});

module.exports = { mapToArray, normalizeRoomData, assembleStructureWithRooms };
